import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  RefreshCw,
  Send,
  CheckCircle,
  AlertCircle,
  Loader,
  X,
  Folder,
  DollarSign,
  Users,
  Home,
  CheckCircle2,
} from "lucide-react";
import StatementTable from "../components/StatementTable";
import BillsChart from "../components/BillsChart";
import { api } from "../api";

function SummaryCard({ label, value, sub, icon: IconComponent, colorClass }) {
  return (
    <div className="glass-panel p-5 flex justify-between items-center transition-all duration-300 hover:-translate-y-0.5 select-none">
      <div>
        <div className="text-[10px] uppercase tracking-[0.08em] font-extrabold text-text-muted mb-1">
          {label}
        </div>
        <div className="font-mono text-[23px] font-extrabold text-text-primary leading-tight">
          {value}
        </div>
        {sub && <div className="text-[11.5px] text-text-muted mt-1.5 font-medium">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 border ${colorClass} transition-colors duration-300`}>
        <IconComponent size={18} />
      </div>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      className="fixed bottom-6 right-6 px-4 py-3 rounded-2xl text-[13px] font-bold flex items-center gap-2 z-[999] shadow-lg animate-fade-in border"
      style={{
        background: type === "error" ? "var(--danger-light)" : "var(--surface)",
        borderColor: type === "error" ? "rgba(239, 68, 68, 0.25)" : "var(--border)",
        color: type === "error" ? "var(--danger)" : "var(--text-primary)",
      }}
    >
      {type === "error" ? (
        <AlertCircle size={15} className="shrink-0" />
      ) : (
        <CheckCircle2 size={15} className="shrink-0 text-emerald-500" />
      )}
      <span>{message}</span>
    </div>
  );
}

function ConfirmModal({ onConfirm, onCancel, bill }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[900] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(12px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="relative w-full max-w-md rounded-3xl p-6 my-auto animate-fade-in bg-surface border border-border shadow-2xl transition-all duration-300">
        {/* Close Button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 p-1.5 rounded-xl text-text-muted hover:text-text-primary bg-surface-2 border border-border hover:border-text-muted transition-all active:scale-95"
        >
          <X size={14} />
        </button>

        {/* Dynamic Icon */}
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-4 bg-sky-50 text-sky-600 dark:bg-sky-950/30 dark:text-sky-400 border border-sky-100 dark:border-sky-900/30">
          <Send size={16} />
        </div>

        {/* Heading */}
        <h2 className="font-display text-[17px] font-extrabold text-text-primary tracking-tight mb-1.5 uppercase">
          Finalize Statement?
        </h2>
        <p className="text-[12.5px] text-text-secondary leading-relaxed mb-4">
          This will finalize the statement splits, save a beautifully formatted <strong className="font-extrabold text-text-primary">Gmail draft</strong> addressed to your tenant, and schedule a <strong className="font-extrabold text-text-primary">Google Calendar reminder</strong> 10 days prior to the due date. This action cannot be undone.
        </p>

        {/* Statement Box Summary */}
        {bill && (
          <div className="rounded-2xl p-3.5 mb-5 flex justify-between items-center bg-surface-2 border border-border">
            <div>
              <div className="text-[9.5px] text-text-muted uppercase tracking-wider font-extrabold mb-0.5">
                Statement Date
              </div>
              <div className="font-mono text-[13px] font-bold text-text-primary">
                {bill.bill_date}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9.5px] text-text-muted uppercase tracking-wider font-extrabold mb-0.5">
                Tenant Total Due
              </div>
              <div className="font-mono text-[16px] font-extrabold text-sky-600 dark:text-sky-400">
                ${bill.totals?.tenant_total?.toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Button Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 rounded-xl text-[12.5px] font-bold text-text-secondary bg-surface-2 border border-border hover:bg-surface hover:text-text-primary hover:border-text-muted transition-all active:scale-[0.98]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2 rounded-xl text-[12.5px] font-bold text-white bg-sky-600 hover:bg-sky-700 shadow-lg shadow-sky-600/10 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
          >
            <Send size={12} />
            Yes, Finalize statement
          </button>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "statement", label: "Statement details" },
  { id: "history", label: "Cost distribution" },
];

export default function Dashboard() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("statement");
  const [searchParams, setSearchParams] = useSearchParams();

  const showToast = (message, type = "success") => setToast({ message, type });

  const loadBills = useCallback(async () => {
    try {
      const data = await api.getBills();
      setBills(data);
    } catch (e) {
      showToast("Failed to load bills", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const billIdParam = searchParams.get("bill");
  const latestBill = billIdParam
    ? bills.find((b) => b.id === billIdParam) ?? bills[0]
    : bills[0];

  const handlePoll = async () => {
    setPolling(true);
    try {
      await api.manualPoll();
      showToast("Monitored Drive folder checked successfully!");
      await loadBills();
    } catch (e) {
      showToast("Drive check failed: " + e.message, "error");
    } finally {
      setPolling(false);
    }
  };

  const handleSplitChange = async (elec, other) => {
    setSaving(true);
    try {
      await api.updateSplits(latestBill.id, { electricity_split: elec, other_split: other });
      showToast("Split ratios updated successfully!");
      await loadBills();
    } catch (e) {
      showToast("Failed to recalculate splits: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizeConfirmed = async () => {
    setShowConfirm(false);
    setFinalizing(true);
    try {
      await api.finalizeBill(latestBill.id);
      showToast("Statement finalized! Gmail draft saved and Calendar reminder added.");
      await loadBills();
    } catch (e) {
      showToast("Finalize failed: " + e.message, "error");
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader size={30} className="animate-spin text-text-muted" />
      </div>
    );
  }

  if (!latestBill) {
    return (
      <div className="text-center px-6 py-20 glass-panel">
        <h2 className="font-display text-[26px] mb-3 text-text-primary font-extrabold tracking-tight uppercase">
          No statement bills uploaded
        </h2>
        <p className="text-text-muted mb-8 text-[14px] leading-relaxed max-w-md mx-auto">
          Please upload your ENMAX or City of Calgary utility statements in PDF format into your monitored Google Drive folder.
        </p>
        <button
          onClick={handlePoll}
          disabled={polling}
          className={`glass-button glass-button-primary px-6 py-3 text-[13.5px] font-bold ${
            polling ? "opacity-60" : ""
          }`}
        >
          <RefreshCw size={14} className="mr-2 animate-spin" />
          {polling ? "Checking Drive…" : "Scan monitored folder"}
        </button>
      </div>
    );
  }

  // Summary aggregates calculations
  const totalTenantShare = bills.reduce((sum, b) => sum + (b.totals?.tenant_total ?? 0), 0);
  const totalLandlordShare = bills.reduce(
    (sum, b) => sum + ((b.totals?.grand_total ?? 0) - (b.totals?.tenant_total ?? 0)),
    0
  );

  let dateRangeLabel = "No bills";
  if (bills.length > 0) {
    const sorted = [...bills].sort((a, b) => a.bill_date.localeCompare(b.bill_date));
    const fmtDate = (ym) => {
      if (!ym) return "";
      const [y, m] = ym.split("-");
      return new Date(Number(y), Number(m) - 1).toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
    };
    dateRangeLabel = `${fmtDate(sorted[0].bill_date)} - ${fmtDate(sorted[sorted.length - 1].bill_date)}`;
  }

  return (
    <div className="animate-fade-in relative z-10 space-y-6">
      {/* Stateful Confirm Overlay Modal */}
      {showConfirm && (
        <ConfirmModal
          bill={latestBill}
          onConfirm={handleFinalizeConfirmed}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Warning when viewing older statement files */}
      {billIdParam && bills[0] && billIdParam !== bills[0].id && (
        <div className="glass-panel p-3.5 px-4 text-[13px] font-bold flex justify-between items-center bg-amber-500/5 border-amber-500/20 text-amber-600 dark:text-amber-400">
          <span>Viewing historical statement invoice (older file).</span>
          <button
            onClick={() => setSearchParams({})}
            className="glass-button glass-button-primary px-3 py-1 text-[11.5px] font-bold"
          >
            Show current statement
          </button>
        </div>
      )}

      {/* Aggregate Cost Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Tracked Bills"
          value={bills.length.toString()}
          sub={dateRangeLabel}
          icon={Folder}
          colorClass="bg-blue-50 text-blue-600 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/20 border-blue-100"
        />
        <SummaryCard
          label="Latest Bill Split"
          value={`$${latestBill.totals?.grand_total?.toFixed(2)}`}
          sub={
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-extrabold text-[11px]">
              <CheckCircle2 size={11} className="shrink-0" /> Cleanly extracted
            </span>
          }
          icon={DollarSign}
          colorClass="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/20 border-emerald-100"
        />
        <SummaryCard
          label="Total Tenant Share"
          value={`$${totalTenantShare.toFixed(2)}`}
          sub="Sum of all loaded bills"
          icon={Users}
          colorClass="bg-sky-50 text-sky-600 dark:bg-sky-950/20 dark:text-sky-400 dark:border-sky-900/20 border-sky-100"
        />
        <SummaryCard
          label="Total Landlord Share"
          value={`$${totalLandlordShare.toFixed(2)}`}
          sub="Includes late payment charges"
          icon={Home}
          colorClass="bg-purple-50 text-purple-600 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900/20 border-purple-100"
        />
      </div>

      {/* Operations Toolbar */}
      <div className="flex justify-between items-center flex-wrap gap-3 pb-3 border-b border-border">
        <div>
          <h2 className="font-display text-[15px] font-extrabold text-text-primary tracking-wider uppercase leading-none">
            {billIdParam && bills[0] && billIdParam !== bills[0].id
              ? "Historical Statement Record"
              : "Active Statement Invoicing"}
          </h2>
          <div className="text-[11.5px] text-text-muted mt-1.5 flex items-center gap-2.5 font-bold uppercase transition-colors duration-300">
            Statement Date: {latestBill.bill_date}
            {latestBill.finalized ? (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-900/20 px-2 py-0.5 rounded-full text-[9px] font-bold">
                <CheckCircle size={10} /> Finalized
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200/50 dark:border-amber-900/20 px-2 py-0.5 rounded-full text-[9px] font-bold animate-pulse">
                • Draft statement
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handlePoll}
            disabled={polling}
            className="glass-button glass-button-secondary px-3.5 py-2 text-[12.5px] font-bold"
          >
            <RefreshCw size={13} className={`mr-1.5 ${polling ? "animate-spin text-text-muted" : "text-text-muted"}`} />
            {polling ? "Checking Drive…" : "Check Drive"}
          </button>
          {!latestBill.finalized && (
            <button
              onClick={() => setShowConfirm(true)}
              disabled={finalizing}
              className="glass-button glass-button-primary px-4 py-2 text-[12.5px] font-bold flex items-center gap-1.5"
            >
              <Send size={12} />
              {finalizing ? "Finalizing…" : "Finalize & Draft"}
            </button>
          )}
        </div>
      </div>

      {/* Content Tab Pill Selectors */}
      <div className="flex gap-1.5 p-1 bg-slate-100 dark:bg-surface-2 border border-slate-200 dark:border-border rounded-xl w-fit transition-colors duration-300">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2 rounded-lg text-[13px] font-bold transition-all duration-200 ${
              activeTab === tab.id
                ? "bg-sky-600 text-white shadow-sm"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Mounted Content Panels */}
      {activeTab === "statement" && (
        <div className="animate-fade-in space-y-4">
          <StatementTable
            bill={latestBill}
            editable={!latestBill.finalized}
            onSplitChange={handleSplitChange}
            isSaving={saving}
          />
          {latestBill.finalized && (
            <div className="p-4 px-5 rounded-2xl text-[13px] font-semibold flex items-center gap-2.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 shadow-sm transition-all duration-300 animate-fade-in">
              <CheckCircle2 size={15} className="shrink-0" />
              <span>Email draft automatically pre-formatted in Gmail · Calendar reminder set 10 days before due date.</span>
            </div>
          )}
        </div>
      )}

      {activeTab === "history" && (
        <div className="animate-fade-in">
          <BillsChart bills={bills} />
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
